import React, {useCallback, useEffect, useMemo} from 'react';
import {LayoutDesigner} from 'config/entity/menu/LayoutDesigner';
import {Nav} from 'react-bootstrap';
import {Icon} from 'shared/helpers';
import {Link} from 'shared/reload/Link';
import styled from 'styled-components';
import {ProductSelector} from 'components/customer/RoomPlanner/components/Menu/ProductSelector';
import {ApplianceSelector} from 'components/customer/RoomPlanner/components/Menu/ApplianceSelector';
import {
    MenuItem,
    useMenuContext,
} from 'components/customer/RoomPlanner/components/Menu/MenuContext';
import {Preview} from 'components/customer/RoomPlanner/components/Menu/Preview';
import {Hardware} from 'components/customer/RoomPlanner/components/Menu/Hardware';
import {Materials} from 'components/customer/RoomPlanner/components/Menu/Materials';
import {useSearchParams} from 'react-router-dom';
import {useAppSelector} from 'store/customer';
import {
    getIsEditingRoomDimension,
    getisRoomPlannerEditing,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {getHasWebGLError} from 'components/customer/Preview3DCommon/store/viewerSlice';
import useUI from 'shared/useUI';
import {StyledDiv} from 'shared/components/MenuButton';
import {
    mobileLandscape,
    mobileLandscapeAndPortraitDevices,
    portraitDevices,
    tablet,
    tabletLandscape,
} from 'shared/Breakpoints';
import {
    selectPageRestricted,
    selectRestrictionMessageDismissed,
} from 'store/customer/appSlice';
import {
    useIsPortraitDevices,
    useIsTouchDevices,
} from 'shared/helpers/DeviceSize';

export interface MenuProps {
    menu: LayoutDesigner[];
    isFooter?: boolean;
    isCollapsed: boolean;
    mobileView?: boolean;
}

export const MenuComponent = ({
    menu,
    isFooter = false,
    isCollapsed,
    mobileView,
}: MenuProps) => {
    const isTouchDevice = useIsTouchDevices();
    const {isSmallScreenLayout} = useUI();
    const {selectedMenu, hideMenu, handleClick, closeProductEditorMenu} =
        useMenuContext();
    const isRestricted = useAppSelector(selectPageRestricted);
    const messageDismissed = useAppSelector(selectRestrictionMessageDismissed);

    const [searchParams] = useSearchParams();

    const isRoomPlannerEditing = useAppSelector(getisRoomPlannerEditing);
    const isEditingRoomDimension = useAppSelector(getIsEditingRoomDimension);
    const isPortraitDevices = useIsPortraitDevices();
    const hasWebGLError = useAppSelector(getHasWebGLError);

    // we need to detect route changes so we can hide or show the drawer
    const productIsSelected = useMemo(() => {
        return !!searchParams.get('product');
    }, [searchParams]);
    const placeholderEditorOpen = useMemo(() => {
        return (
            !!searchParams.get('placeholderEdit') ||
            !!searchParams.get('placeholderFace')
        );
    }, [searchParams]);

    useEffect(() => {
        if (!isRoomPlannerEditing) {
            hideMenu();
        }
    }, [isRoomPlannerEditing]);

    const renderRoomPlannerMenu = useMemo(() => {
        if (
            selectedMenu === '' &&
            !isRoomPlannerEditing &&
            !productIsSelected &&
            !placeholderEditorOpen
        ) {
            return null;
        }

        if (isEditingRoomDimension) {
            return null;
        }

        if (hasWebGLError) {
            return null;
        }

        const activeMenu =
            productIsSelected || placeholderEditorOpen || isRoomPlannerEditing
                ? selectedMenu ||
                  (placeholderEditorOpen
                      ? MenuItem.ADD_APPLIANCE
                      : MenuItem.ADD_PRODUCT)
                : selectedMenu;

        const menuComponents: Partial<Record<MenuItem, JSX.Element>> = {
            [MenuItem.ADD_PRODUCT]: (
                <>
                    <div>
                        <ProductSelector />
                    </div>
                    <Preview />
                </>
            ),
            [MenuItem.ADD_APPLIANCE]: (
                <div>
                    <ApplianceSelector />
                </div>
            ),
            [MenuItem.HARDWARE_SELECTIONS]: <Hardware />,
            [MenuItem.MATERIALS]: <Materials />,
        };

        return menuComponents[activeMenu as MenuItem] || null;
    }, [
        selectedMenu,
        productIsSelected,
        placeholderEditorOpen,
        isRoomPlannerEditing,
        isEditingRoomDimension,
        hasWebGLError,
    ]);

    const closePanel = useCallback(() => {
        if (
            selectedMenu === MenuItem.ADD_PRODUCT ||
            selectedMenu === MenuItem.ADD_APPLIANCE
        ) {
            if (closeProductEditorMenu) {
                closeProductEditorMenu();
            }
            return;
        }

        if (
            selectedMenu === MenuItem.HARDWARE_SELECTIONS ||
            selectedMenu === MenuItem.MATERIALS
        ) {
            handleClick(selectedMenu)();
        }
    }, [closeProductEditorMenu, handleClick, selectedMenu]);

    if (menu === undefined) return null;

    if (isEditingRoomDimension && isPortraitDevices) {
        return null;
    }

    return (
        <>
            <NavLocal className="flex-column job-menu">
                {menu.map((item) => (
                    <NavLink
                        as={Link}
                        key={item.name}
                        to={undefined}
                        className={`${
                            selectedMenu == item.event ? 'active' : ''
                        }`}
                        onClick={handleClick(item.event)}>
                        <StyledDiv
                            $isSmallDevice={
                                !isTouchDevice && isSmallScreenLayout
                            }>
                            <Icon iconName={item.icon} title={item.name} />
                        </StyledDiv>
                        {!isCollapsed || mobileView ? (
                            <label>{item.name}</label>
                        ) : null}
                    </NavLink>
                ))}
            </NavLocal>
            {!isFooter ? (
                <RoomPlannerMenu
                    $hasTopOffset={(isRestricted && !messageDismissed) || false}
                    $display={!!renderRoomPlannerMenu}
                    $isCollapsed={isCollapsed}
                    $isSmallDevice={isSmallScreenLayout}>
                    {renderRoomPlannerMenu}
                    <OutsideClose type="button" onClick={closePanel}>
                        <Icon iconName="Button-Cancel.svg" />
                    </OutsideClose>
                </RoomPlannerMenu>
            ) : null}
        </>
    );
};

const NavLocal = styled(Nav)`
    & > a {
        text-decoration: none;
    }
    &:hover {
        background-color: transparent !important;
    }

    ${portraitDevices`
        position: fixed;
        z-index: 100;
        bottom: 0;
        left: 0;
        right: 0;
        height: 100px;
        background: white !important;
        box-shadow: 0 0 8px 1px #adadad;
        flex-direction: row !important;
        justify-content: space-around;
        align-items: flex-start;
        padding-top: 15px;

        &:hover {
            background: white !important;
        }

        > a {
            padding: 0;
            margin: 0;

            > div > img {
                width: 35px;
            }
        }
    `}

    ${tablet`
        height: 80px;
    `}

    ${mobileLandscape`
        height: 100%;
        position: initial;
        background: transparent !important;
        right: initial;
        flex-direction: column !important;
        padding-top: 0;
        box-shadow: none;
        width: initial;
        align-items: initial;
        justify-content: space-around;

        > a {
            width: initial;
            > div {
                width: 50px !important;
                > img {
                    width: 2vw;
                }
            }
        }
    `}
`;

const NavLink = styled(Nav.Link)`
    ${({theme}) => {
        return `
            color: ${theme.colors.primary.main};
        `;
    }}

    ${portraitDevices`
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: center;
        text-decoration: none;
        flex: 1 1 0;
        min-width: 0;
        width: 0;
        overflow: hidden;
        padding: 0 2px;

        > label {
            display: block;
            width: 100%;
            margin: 0;
            font-size: 0.6em;
            font-weight: bold;
            line-height: 1.15;
            text-align: center;
            white-space: normal;
            overflow-wrap: anywhere;
        }
    `}

    ${mobileLandscape`
        flex-direction: row;

        > label {
            text-align: left;
        }
    `}
`;

type RoomPlannerMenuProps = {
    $isCollapsed: boolean;
    $isSmallDevice?: boolean;
    $display: boolean;
    $hasTopOffset: boolean;
};

const OutsideClose = styled.button`
    position: absolute;
    top: 12px;
    left: 363px;
    z-index: 101;
    padding: 0;
    border: 0;
    background: transparent;
    line-height: 0;
    cursor: pointer;

    > img {
        width: 20px;
        border-radius: 20px;
        border: 1px solid #6d6e71;
        background: white;
    }

    ${mobileLandscapeAndPortraitDevices`
        top: 8px;
        left: auto;
        right: 8px;
    `}
`;

const RoomPlannerMenu = styled.div<RoomPlannerMenuProps>`
    position: fixed;
    top: 166px;
    left: ${({$isCollapsed, $isSmallDevice}) => {
        if (!$isSmallDevice) {
            return $isCollapsed ? '95px' : '250px';
        } else {
            return $isCollapsed ? '60px' : '215px';
        }
    }};
    display: flex;
    gap: 10px;
    background-color: transparent !important;
    align-items: flex-start;

    > div:first-child {
        width: 400px;

        .nav-and-control {
            width: 400px;
            right: initial;
            border-bottom-left-radius: 8px !important;
            border-bottom-right-radius: 8px !important;
        }
    }

    > div {
        background-color: white !important;
        box-shadow: 2px 2px 10px -1px #bdbdbd;
        border-radius: 8px;
        padding: 0 15px 15px;
        max-width: 400px;
        max-height: calc(100vh - 174px);
        overflow-y: auto;
        overflow-x: hidden;
    }

    ${mobileLandscapeAndPortraitDevices`
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 99999;
        background-color: white !important;

        > div {
            border-radius: 0;
            max-height: 100%;
            width: 100% !important;
            max-width: initial;
            box-shadow: none;
        }

        .productSearch {
            width: 100% !important;
        }

        >div:first-child .nav-and-control {
            width: 100% !important;
            border-bottom-left-radius: 0 !important;
            border-bottom-right-radius: 0 !important;
        }

        .wizard-form-container {
            padding: 0 15px !important;
        }

        .control-buttons {
            padding: 15px 0;
        }
    `}

    ${mobileLandscape`
        width: 50%;
        left: 46px;
    `}

    ${({$display}) => {
        if (!$display) {
            return `display: none;`;
        }
    }}

    ${tabletLandscape<RoomPlannerMenuProps>`
        bottom: 5px;

        ${({$hasTopOffset}) => {
            if ($hasTopOffset) {
                return `
                top: 221px;

                > div {
                    max-height: calc(100vh - 231px);
                }
            `;
            }
        }};
    `}
`;
